import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '@/common/auth-routes';
import { buildTenantPath } from '@/common/tenant-utils';
import { TenantContext } from '@/contexts/tenant-context-definition';

/**
 * Custom navigation hook that provides tenant-aware navigation utilities
 * All paths are prefixed with the current tenant slug for path-based routing
 */
export const useNavigation = () => {
  const navigate = useNavigate();
  const tenant = useContext(TenantContext)?.tenant;

  const navigateToTenantSelection = useCallback(() => {
    navigate(ROUTES.TENANT_SELECTION);
  }, [navigate]);

  const navigateToTenant = useCallback(
    (tenantSlug: string) => {
      navigate(`/${tenantSlug}`);
    },
    [navigate]
  );

  const navigateToDashboard = useCallback(() => {
    if (tenant) {
      navigate(buildTenantPath(tenant, ROUTES.DASHBOARD));
    }
  }, [tenant, navigate]);

  const navigateToProfile = useCallback(() => {
    if (tenant) {
      navigate(buildTenantPath(tenant, ROUTES.PROFILE));
    }
  }, [tenant, navigate]);

  const navigateToPath = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  // Helper to replace current route (like window.location.replace)
  const replaceRoute = useCallback(
    (path: string) => {
      navigate(path, { replace: true });
    },
    [navigate]
  );

  return {
    navigate,
    navigateToTenantSelection,
    navigateToTenant,
    navigateToDashboard,
    navigateToProfile,
    navigateToPath,
    replaceRoute,
    tenant,
  };
};
